import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel
} from "@tanstack/react-table";

import numberFormat from "../shared/number_format";

// ─────────────────────────────────────────────────────────────────────────────
// Inner headless table (TanStack v8). The outer BasicReactTableWrapper class
// computes the columns/data/controlled-state (and is subclassed by the tariff
// tables which override its instance methods), then delegates the actual render
// here so the v8 HOOK can run in a function component. This emits the SAME
// `.ReactTable` / `.rt-*` DOM that react-table v6 auto-generated, because all
// table styling lives in the app SCSS (_new_tariff_survey_hub.scss /
// _regulations.scss) and targets those exact class names.
// ─────────────────────────────────────────────────────────────────────────────
function TanstackBasicTable(props) {
  const {
    data,
    columns,
    getTdProps,
    getTrGroupProps,
    subComponent,
    className,
    defaultSorted,
    defaultPageSize,
    manual,
    pages: manualPages,
    page: controlledPage,
    onPageChange,
    sorted: controlledSorted,
    onSortedChange,
    expanded
  } = props;

  // Visible columns only (v6 `show:false` ⇒ omit from render). Keep the original
  // index on each so getTdProps/className/labelColumnIndex stay index-correct.
  const visibleColumns = columns.filter(c => c.show !== false && !c.expander);

  // `manual` (overview / standards_list) means the server already sorted+paged the
  // data — the table just renders the page it was handed, NO client-side sort/page
  // row model. Non-manual consumers (tariff tables, country_regulations_table) get
  // the v8 client sort + pagination row models.
  const isManual = !!manual;

  // Sorting STATE: controlled when the consumer passes `sorted` + `onSortedChange`
  // (overview / standards_list / country_regulations_table), otherwise internal
  // seeded from defaultSorted. v6 sorted shape == v8 SortingState ({id,desc}).
  // (Where the state lives is independent of whether v8 sorts the rows — that's
  // governed by `manual` above.)
  const isControlledSort = typeof onSortedChange === "function";
  const [internalSorting, setInternalSorting] = React.useState(defaultSorted || []);
  const sorting = isControlledSort ? (controlledSorted || []) : internalSorting;

  // Pagination STATE: controlled when the consumer passes `page` + `onPageChange`.
  const pageSize = defaultPageSize || 6;
  const isControlledPage = typeof onPageChange === "function";
  const [internalPageIndex, setInternalPageIndex] = React.useState(0);
  const pageIndex = isControlledPage ? (controlledPage || 0) : internalPageIndex;

  const table = useReactTable({
    data,
    columns: visibleColumns.map(c => ({
      id: c.accessor,
      // accessorFn (not accessorKey) so the `td-N` key is read verbatim and not
      // parsed as a deep/dotted path.
      accessorFn: row => row[c.accessor],
      header: c.Header,
      enableSorting: c.sortable !== false,
      sortDescFirst: !!c.defaultSortDesc,
      sortingFn: c.sortMethod
        ? (rowA, rowB) =>
            c.sortMethod(rowA.getValue(c.accessor), rowB.getValue(c.accessor))
        : "auto",
      meta: {
        index: c.index,
        className: c.className,
        headerClassName: c.headerClassName,
        minWidth: c.minWidth,
        defaultSortDesc: c.defaultSortDesc
      }
    })),
    state: {
      sorting,
      ...(isManual ? {} : { pagination: { pageIndex, pageSize } })
    },
    manualSorting: isManual,
    manualPagination: isManual,
    pageCount: isManual ? manualPages : undefined,
    onSortingChange: updater => {
      const next =
        typeof updater === "function" ? updater(sorting) : updater;
      if (isControlledSort) {
        onSortedChange(next);
      } else {
        setInternalSorting(next);
      }
    },
    // Supply a pagination handler whenever we control pagination state, so v8
    // never logs the "controlled state without an on*Change" dev console.error
    // (that would trip the spec-suite JS-console gate). Routes to goToPage below.
    onPaginationChange: isManual
      ? undefined
      : updater => {
          const prev = { pageIndex, pageSize };
          const next = typeof updater === "function" ? updater(prev) : updater;
          if (next.pageIndex !== pageIndex) {
            if (isControlledPage) {
              onPageChange(next.pageIndex);
            } else {
              setInternalPageIndex(next.pageIndex);
            }
          }
        },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: isManual ? undefined : getSortedRowModel(),
    getPaginationRowModel: isManual ? undefined : getPaginationRowModel(),
    autoResetExpanded: false,
    autoResetPageIndex: false
  });

  // Rows for the current page. Manual paging ⇒ the data IS already the page.
  const rows = isManual
    ? table.getRowModel().rows
    : table.getPaginationRowModel().rows;

  const totalPages = isManual
    ? manualPages
    : table.getPageCount();

  const goToPage = newIndex => {
    if (isControlledPage) {
      onPageChange(newIndex);
    } else {
      setInternalPageIndex(newIndex);
    }
  };

  // v6-parity page-jump box (v6 `showPageJump` default true): typing holds a
  // transient value; blur/Enter clamps to [1..totalPages] and commits.
  const [typedPage, setTypedPage] = React.useState(null);
  const commitTypedPage = () => {
    if (typedPage !== null) {
      const n = parseInt(typedPage, 10);
      if (!isNaN(n)) {
        goToPage(Math.min(Math.max(n, 1), totalPages || 1) - 1);
      }
      setTypedPage(null);
    }
  };

  const headerCells = table.getFlatHeaders().map(header => {
    const meta = header.column.columnDef.meta || {};
    const canSort = header.column.getCanSort();
    const sortDir = header.column.getIsSorted(); // false | "asc" | "desc"
    const thClass = classNames(
      "rt-th",
      meta.headerClassName,
      canSort && "-cursor-pointer",
      sortDir === "desc" && "-sort-desc",
      sortDir === "asc" && "-sort-asc"
    );

    const toggle = canSort
      ? e => {
          // v6 default: descending first for numeric columns.
          header.column.toggleSorting(undefined, e.shiftKey);
        }
      : undefined;

    return (
      <div
        key={header.id}
        className={thClass}
        style={meta.minWidth ? { minWidth: meta.minWidth } : undefined}
        onClick={toggle}
      >
        <div className="rt-resizable-header-content">
          {header.isPlaceholder ? null : header.column.columnDef.header}
        </div>
      </div>
    );
  });

  const body = rows.map((row, viewIndex) => {
    const rowInfo = {
      index: row.index,
      viewIndex,
      row: row.original,
      original: row.original
    };

    const groupProps = getTrGroupProps
      ? getTrGroupProps({ expanded }, rowInfo, undefined)
      : {};

    const tds = row.getVisibleCells().map(cell => {
      const meta = cell.column.columnDef.meta || {};
      const tdProps = getTdProps
        ? getTdProps({}, rowInfo, { index: meta.index })
        : {};

      // v6 spread the td props as DOM props; the wrapper's handlers expect
      // (e, handleOriginal). Adapt onClick/onMouseOver so handleOriginal is a noop.
      const domProps = {};
      Object.keys(tdProps).forEach(k => {
        const v = tdProps[k];
        if (typeof v === "function") {
          domProps[k] = e => v(e, undefined);
        } else {
          domProps[k] = v;
        }
      });

      return (
        <div
          key={cell.id}
          className={classNames("rt-td", meta.className)}
          style={meta.minWidth ? { minWidth: meta.minWidth } : undefined}
          {...domProps}
        >
          {cell.getValue()}
        </div>
      );
    });

    const isExpanded = !!(expanded && expanded[viewIndex]);

    return (
      <div className="rt-tr-group" key={row.id} {...groupProps}>
        <div className={classNames("rt-tr", viewIndex % 2 ? "-even" : "-odd")}>
          {tds}
        </div>
        {subComponent && isExpanded ? subComponent(rowInfo) : null}
      </div>
    );
  });

  // v6-parity pad rows: v6 filled every page to `minRows` (default = pageSize)
  // with `-padRow` rows whose zebra parity continues from the real rows; the
  // vendored hover rule (`.-highlight … .rt-tr:not(.-padRow):hover`) already
  // excludes them, as v6 did.
  const padRows = Array.from({
    length: Math.max(pageSize - rows.length, 0)
  }).map((_, i) => (
    <div className="rt-tr-group" key={`pad-${i}`}>
      <div
        className={classNames(
          "rt-tr",
          "-padRow",
          (rows.length + i) % 2 ? "-even" : "-odd"
        )}
      >
        {visibleColumns.map((c, j) => (
          <div
            key={j}
            className={classNames("rt-td", c.className)}
            style={c.minWidth ? { minWidth: c.minWidth } : undefined}
          >
            <span>&nbsp;</span>
          </div>
        ))}
      </div>
    </div>
  ));

  return (
    <div className={classNames("ReactTable", className)}>
      <div className="rt-table">
        <div className="rt-thead -header">
          <div className="rt-tr">{headerCells}</div>
        </div>
        <div className="rt-tbody">
          {body}
          {padRows}
        </div>
      </div>
      {/* v6 rendered the pagination bar unconditionally (showPagination default
          true, no page-count gate) — "Page [1] of 1" with both buttons disabled
          on a single page, and a -pageJump number input, as prod had. */}
      <div className="pagination-bottom">
        <div className="-pagination">
          <div className="-previous">
            <button
              type="button"
              className="-btn"
              disabled={pageIndex <= 0}
              onClick={() => goToPage(pageIndex - 1)}
            >
              Previous
            </button>
          </div>
          <div className="-center">
            {/* v6 emitted -pageInfo as a <span> with the -pageJump <div> inside
                (invalid nesting React dev builds warn about); a <div> renders
                identically — the vendored CSS pins display:inline-block. */}
            <div className="-pageInfo">
              Page{" "}
              <div className="-pageJump">
                <input
                  type="number"
                  value={typedPage !== null ? typedPage : pageIndex + 1}
                  onChange={e => setTypedPage(e.target.value)}
                  onBlur={commitTypedPage}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      commitTypedPage();
                      e.target.blur();
                    }
                  }}
                />
              </div>{" "}
              of <span className="-totalPages">{totalPages || 1}</span>
            </div>
          </div>
          <div className="-next">
            <button
              type="button"
              className="-btn"
              disabled={pageIndex >= totalPages - 1}
              onClick={() => goToPage(pageIndex + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

TanstackBasicTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  getTdProps: PropTypes.func,
  getTrGroupProps: PropTypes.func,
  subComponent: PropTypes.func,
  className: PropTypes.string,
  defaultSorted: PropTypes.array,
  defaultPageSize: PropTypes.number,
  manual: PropTypes.bool,
  pages: PropTypes.number,
  page: PropTypes.number,
  onPageChange: PropTypes.func,
  sorted: PropTypes.array,
  onSortedChange: PropTypes.func,
  expanded: PropTypes.object
};

class BasicReactTableWrapper extends React.Component {
  // eslint-disable-next-line no-unused-vars
  suffix(column, i) {
    return;
  }

  // eslint-disable-next-line no-unused-vars
  decimals(column, i) {
    if (typeof(column.decimals) === "undefined") {
      return 2;
    } else {
      return column.decimals;
    }
  }

  format(column, i, tdContent, lci) {
    if (lci(i)) { return this.formatLabelTd(tdContent); }
    if (tdContent === null) { return "–"; }

    let formatted = numberFormat(this.decimals, this.suffix, column, i).to(tdContent);

    if (column && column.decimals) {
      formatted = formatted.toString();
    }

    return formatted;
  }

  // eslint-disable-next-line no-unused-vars
  handleLabelColumnMouseOver(labelTdContent) {
  }

  formatLabelTd(rawTdContent) {
    return rawTdContent;
  }

  extractFormattedLabelTd(formattedTdContent) {
    return formattedTdContent;
  }

  labelColumnIndex(i) {
    return i === 0;
  }

  defaultSorted() {
    return [];
  }

  render() {
    if (!this.props.data || !this.props.data[0]) { return <div></div>; }
    const reactTableProps = this.props.reactTableProps || {};
    const hasNestedRows = !!this.props.subComponent;

    const lci = this.props.labelColumnIndex || this.labelColumnIndex;

    const headerRow = this.props.data[0];
    const dataRows = this.props.data.slice(1);

    const key = i => `td-${i}`;

    const minWidths = this.props.minWidths || {};
    const unsortableColumns = this.props.unsortableColumns || [];

    const columns = headerRow.map((thContent, i) => ({
      Header: thContent,
      accessor: key(i),
      headerClassName: classNames({ "text-end": !lci(i), "text-start": lci(i) }),
      className: classNames({ "text-end": !lci(i) }),
      minWidth: minWidths[i] || 40,
      index: i,
      defaultSortDesc: !lci(i),
      show: thContent !== "5-Year CAGR",
      sortable: !unsortableColumns.includes(i),
      decimals: this.props.decimals
    }));

    const sortMethod = (column, i) => {
      if (lci(i)) { return; }

      const fromFormat = numberFormat(this.decimals, this.suffix, column, i).from;

      return (a, b) => (fromFormat(a) - fromFormat(b));
    };

    columns.map((column, i) => {
      column.sortMethod = sortMethod(column, i);
    });


    if (hasNestedRows) {
      columns.push({
        expander: true,
        index: headerRow.length
      });
    }

    const rowReducer = (rowObject, tdContent, i) => {
      rowObject[key(i)] = this.format(columns[i], i, tdContent, lci);

      return rowObject;
    };
    const rowToObject = (row) => (row.reduce(rowReducer, {}));
    const data = dataRows.map(rowToObject);

    const handleTdClick = this.props.handleTdClick || (() => {});
    const handleLabelColumnMouseOver = this.handleLabelColumnMouseOver;
    const extractFormattedLabelTd = this.extractFormattedLabelTd;
    const runIfFn = (candidate) => {
      if (typeof candidate === "function") {
        candidate();
      }
    };

    // TODO(SI): we might need to pass more than just one label value in the future.
    const firstLabelColumnSelector = key(0);
    const customTdProps = (state, rowInfo, column) => {
      const labelColumnMouseOver = (e, handleOriginal) => {
        if (!rowInfo) { return; }

        handleLabelColumnMouseOver(extractFormattedLabelTd(rowInfo.row[firstLabelColumnSelector]));

        runIfFn(handleOriginal);
      };

      const p = { onMouseOver: labelColumnMouseOver };

      if (!lci(column.index)) { return p; }

      const onClick = (e, handleOriginal) => {
        if (!rowInfo) { return; }

        handleTdClick(rowInfo, e);

        runIfFn(handleOriginal);
      };

      p.onClick = onClick;
      return p;
    };

    const extraClasses = {
      "-highlight": hasNestedRows || this.props.addHighlightClass
    };

    return (
      <TanstackBasicTable
        defaultPageSize={6}
        {...reactTableProps}
        getTdProps={customTdProps}
        className={classNames(extraClasses)}
        data={data}
        columns={columns}
        defaultSorted={this.defaultSorted()}
        subComponent={this.props.subComponent}
      />
    );
  }
}

BasicReactTableWrapper.propTypes = {
  data: PropTypes.array,
  subComponent: PropTypes.func,
  labelColumnIndex: PropTypes.func,
  handleTdClick: PropTypes.func,
  minWidths: PropTypes.arrayOf(PropTypes.number),
  unsortableColumns: PropTypes.arrayOf(PropTypes.number),
  reactTableProps: PropTypes.object,
  addHighlightClass: PropTypes.bool,
  decimals: PropTypes.oneOfType([PropTypes.number, PropTypes.bool])
};

export default BasicReactTableWrapper;
